import React, { useContext, useEffect, useReducer, useRef, useState } from 'react';
import { Image, ImageBackground, NativeScrollEvent, RefreshControl, SafeAreaView, ScrollView, StatusBar, View } from 'react-native';
import { Button, Text, useTheme } from 'react-native-paper';
import B from '../../../components/Bold';
import { NavigationWithProps } from '../../../core/Types';
import { PagesNavParamList } from '../Pages';
import { useAppointments } from '../../../core/api/appointments';
import { AppointmentModel, IAppointment } from '../../../core/models/AppointmentModel';
import AppointmentCard from '../../../components/AppointmentCard';
import { Pet } from '../../../assets/images';
import { CardListLoader } from '../../../components/Loaders';
import { AppStoreContext } from '../../../core/provider/AppStoreProvider';
import { useDialog } from '../../../core/hooks/dialog';
import { useTherapists } from '../../../core/api/therapists';
import { ApiErrors, ApiResponse, PaginatedResponse } from '../../../core/models/ApiResponse';
import { AppointmentStatus } from '../../../core/models/enums';
import { Scenes } from '../../../assets/images';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { RootNavParamList } from '../../../../App';
import AppleStatusBarPadding from '../../../components/AppleStatusBarPadding';

function today() {
    const start = new Date();
    const end = new Date(start);

    start.setHours(start.getHours() - 1);
    end.setHours(23, 59, 59, 999);

    return [start, end];
};

const Appointments: React.FC<NavigationWithProps<{}, PagesNavParamList, "Appointments", "AppNavigator">> = ({ navigation, route }) => {

    const theme = useTheme();
    const dialog = useDialog();

    const appointments = useAppointments();
    const therapists = useTherapists();

    const appContext = useContext(AppStoreContext);

    const [todayAppointmentList, setTodayAppointmentList] = useState<PaginatedResponse<AppointmentModel> | undefined>(undefined);
    const [pendingAppointmentList, setPendingAppointmentList] = useState<PaginatedResponse<AppointmentModel> | undefined>(undefined);
    const [pendingPaymentAppointmentList, setPendingPaymentAppointmentList] = useState<PaginatedResponse<AppointmentModel> | undefined>(undefined);
    const [confirmedAppointmentList, setConfirmedAppointmentList] = useState<PaginatedResponse<AppointmentModel> | undefined>(undefined);
    const [completedAppointmentList, setCompletedAppointmentList] = useState<PaginatedResponse<AppointmentModel> | undefined>(undefined);

    const [refreshing, setRefreshing] = useState(false)

    const errorLoading = useRef(false);

    const fetchTodayAppointments = async (page: number = 1) => {
        if (page == 1) {
            setTodayAppointmentList(undefined);
        }

        const [start, end] = today();
        const apps = await appointments.listAppointments({
            statuses: [AppointmentStatus.Confirmed],
            timeSlotOrder: "asc",
            startDate: start,
            endDate: end,
            pageNum: page,
            pageSize: 1
        }).catch((error) => {
            errorLoading.current = true;
            return undefined
        });

        if (!apps) {
            return;
        }

        if (page > 1) {
            apps.items = todayAppointmentList?.items.concat(apps.items) ?? [];
        }

        setTodayAppointmentList(apps);
    }

    const fetchPendingAppointments = async (page: number = 1) => {
        if (page == 1) {
            setPendingAppointmentList(undefined);
        }

        const apps = await appointments.listAppointments({
            statuses: [AppointmentStatus.PendingConfirmation],
            timeSlotOrder: "asc",
            startDate: new Date(),
            pageNum: page,
            pageSize: 10
        }).catch((error) => {
            errorLoading.current = true;
            return undefined
        });

        if (!apps) {
            return;
        }

        if (page > 1) {
            apps.items = pendingAppointmentList?.items.concat(apps.items) ?? [];
        }

        setPendingAppointmentList(apps);
    };

    const fetchPendingPaymentAppointments = async (page: number = 1) => {
        if (page == 1) {
            setPendingPaymentAppointmentList(undefined);
        }

        const apps = await appointments.listAppointments({
            statuses: [AppointmentStatus.PendingPayment],
            timeSlotOrder: "asc",
            startDate: new Date(),
            pageNum: page,
            pageSize: 10
        }).catch((error) => {
            errorLoading.current = true;
            return undefined
        });

        if (!apps) {
            return;
        }

        if (page > 1) {
            apps.items = pendingPaymentAppointmentList?.items.concat(apps.items) ?? [];
        }

        setPendingPaymentAppointmentList(apps);
    };

    const fetchConfirmedAppointments = async (page: number = 1) => {
        if (page == 1) {
            setConfirmedAppointmentList(undefined);
        }

        const [_, end] = today();
        const apps = await appointments.listAppointments({
            statuses: [AppointmentStatus.Confirmed],
            startDate: end,
            timeSlotOrder: "asc",
            pageNum: page,
            pageSize: 10
        }).catch((error) => {
            errorLoading.current = true;
            return undefined
        });

        if (!apps) {
            return;
        }

        if (page > 1) {
            apps.items = confirmedAppointmentList?.items.concat(apps.items) ?? [];
        }

        setConfirmedAppointmentList(apps);
    }

    const fetchCompletedAppointments = async (page: number = 1) => {
        if (page == 1) {
            setCompletedAppointmentList(undefined);
        }

        const [start, _] = today();
        const apps = await appointments.listAppointments({
            statuses: [AppointmentStatus.Confirmed],
            endDate: start,
            timeSlotOrder: "desc",
            pageNum: page,
            pageSize: 10
        }).catch((error) => {
            errorLoading.current = true;
            return undefined
        });

        if (!apps) {
            return;
        }

        if (page > 1) {
            apps.items = completedAppointmentList?.items.concat(apps.items) ?? [];
        }

        setCompletedAppointmentList(apps);
    }

    const onRefresh = async () => {
        setRefreshing(true);
        await Promise.all([
            fetchTodayAppointments(),
            fetchPendingAppointments(),
            fetchPendingPaymentAppointments(),
            fetchConfirmedAppointments(),
            fetchCompletedAppointments()
        ]);
        setRefreshing(false);
    };

    const onappointmentCancel = async (appointment: IAppointment) => {
        const ok = await dialog.openIconDialog('close-circle-outline',
            'Cancelar cita', '¿Estas seguro de que deseas cancelar esta cita?.', "Aceptar", "Salir");

        if (ok) {
            await therapists.cancelAppointment(appointment.clinic_id, appointment.virtual_therapist_id, appointment.id)
                .catch(async (error: Response) => {
                    const apiError = await error.json() as ApiResponse;
                    if (apiError && apiError.error == ApiErrors.CANCELLATION_NOT_ALLOWED) {
                        dialog.openIconDialog("alert-circle-outline",
                            "Cancelación no permitida",
                            "No se puede cancelar esta cita porque no cumple con la política de cancelación. Por favor, contacta a tu terapeuta para más información.",
                            "Aceptar");
                        return;
                    }
                })
                .catch((error: any) => {
                    // TODO: An error occurred(Maybe cancelling an appointment that is not cancellable)
                    // Just refresh the appointments.Additional handling should be added
                    console.error(error);
                    onRefresh();
                });
        }
    };

    const appointmentClick = async (appointment: IAppointment) => {
        const therapist = await therapists.getTherapist(appointment.clinic_id, appointment.virtual_therapist_id);
        if (therapist != undefined) {
            navigation
                .getParent<NativeStackNavigationProp<RootNavParamList>>("AppNavigator")
                ?.navigate("Therapist", therapist);
        }
    };

    const openTherapistProfile = () => {
        if (appContext.myTherapist?.value != undefined) {
            navigation
                .getParent<NativeStackNavigationProp<RootNavParamList>>("AppNavigator")
                ?.navigate("Therapist", appContext.myTherapist?.value.agenda);
        }
    }

    const loadAllAppointments = () => {
        fetchTodayAppointments();
        fetchPendingAppointments();
        fetchPendingPaymentAppointments();
        fetchConfirmedAppointments();
        fetchCompletedAppointments();
    }

    // useEffect(() => {
    //     loadAllAppointments();
    // }, [appContext.myTherapist?.value]);

    //TODO: By the moment we are always refreshing the appointments list when the screen is focused
    // In the future we could update this using some kind of push events
    useEffect(() => {
        navigation.addListener("focus", () => {
            // if (errorLoading.current) {
            //     errorLoading.current = false;
            loadAllAppointments();
            // }
        });
    }, []);

    return (
        <SafeAreaView style={{
            flex: 1,
            backgroundColor: theme.colors.surfaceVariant,
            position: "relative"
        }}>
            <StatusBar
                translucent
                barStyle={theme.dark ? "light-content" : "dark-content"}
                backgroundColor="transparent"
            />
            <ScrollView style={{
                width: "100%",
            }}
                refreshControl={
                    <RefreshControl refreshing={refreshing} onRefresh={onRefresh} />
                }
            >

                <View>
                    <ImageBackground
                        style={{
                            width: "100%",
                            height: 200,
                            gap: 15,
                        }}
                        imageStyle={{ resizeMode: 'cover' }}
                        source={Scenes.readingLivingroom}>
                        <AppleStatusBarPadding blurrEffect />
                    </ImageBackground>

                    <View style={{
                        alignItems: "center",
                        paddingTop: 12,
                        borderTopColor: theme.colors.secondaryContainer,
                        borderTopWidth: 0.5,
                    }}>
                        <Text style={{ fontSize: 25, color: theme.colors.onSurfaceVariant }}>
                            <B>Mis citas</B>
                        </Text>

                        <Button
                            style={{ marginTop: 15 }}
                            onPress={openTherapistProfile}
                            mode='contained'>
                            Solicitar cita
                        </Button>
                    </View>
                </View>

                <View style={{
                    marginTop: 20,
                    paddingHorizontal: 20,
                    paddingTop: 20,
                    borderTopEndRadius: 30,
                    borderTopStartRadius: 30,
                    backgroundColor: theme.colors.surface,
                    minHeight: 490
                }}>

                    {todayAppointmentList == undefined || pendingAppointmentList == undefined || completedAppointmentList == undefined ?
                        <>
                            <CardListLoader />
                            <CardListLoader />
                        </> :
                        <>
                            {todayAppointmentList?.items?.length !== 0 && <>
                                <Text style={{ fontSize: 20, marginBottom: 20 }}><B>Cita para hoy</B></Text>
                                {todayAppointmentList?.items.map((app) => (
                                    <AppointmentCard
                                        key={app.id}
                                        appointment={app}
                                        onCancelPress={onappointmentCancel}
                                        onPress={appointmentClick} />
                                ))}
                            </>}

                            {pendingAppointmentList?.items?.length !== 0 &&
                                <>
                                    <Text style={{ fontSize: 20, marginBottom: 20 }}><B>Pendientes de confirmar</B></Text>
                                    {pendingAppointmentList?.items.map((app) => (
                                        <AppointmentCard
                                            key={app.id}
                                            appointment={app}
                                            onCancelPress={onappointmentCancel}
                                            onPress={appointmentClick} />
                                    ))}
                                    {pendingAppointmentList?.hasNextPage === true && <Button
                                        mode="text"
                                        onPress={() => fetchPendingAppointments(pendingAppointmentList.currentPage + 1)}>
                                        Ver más
                                    </Button>}
                                </>
                            }

                            {pendingPaymentAppointmentList?.items?.length !== 0 &&
                                <>
                                    <Text style={{ fontSize: 20, marginBottom: 20 }}><B>Pendientes de pago</B></Text>
                                    {pendingPaymentAppointmentList?.items.map((app) => (
                                        <AppointmentCard
                                            key={app.id}
                                            appointment={app}
                                            onCancelPress={onappointmentCancel}
                                            onPress={appointmentClick} />
                                    ))}
                                    {pendingPaymentAppointmentList?.hasNextPage === true && <Button
                                        mode="text"
                                        onPress={() => fetchPendingPaymentAppointments(pendingPaymentAppointmentList.currentPage + 1)}>
                                        Ver más
                                    </Button>}
                                </>
                            }

                            {confirmedAppointmentList?.items?.length !== 0 &&
                                <>
                                    <Text style={{ fontSize: 20, marginBottom: 20 }}><B>Confirmadas</B></Text>
                                    {confirmedAppointmentList?.items.map((app) => (
                                        <AppointmentCard
                                            key={app.id}
                                            appointment={app}
                                            onCancelPress={onappointmentCancel}
                                            onPress={appointmentClick} />
                                    ))}
                                    {confirmedAppointmentList?.hasNextPage === true && <Button
                                        mode="text"
                                        onPress={() => fetchConfirmedAppointments(confirmedAppointmentList.currentPage + 1)}>
                                        Ver más
                                    </Button>}
                                </>
                            }

                            {completedAppointmentList?.items?.length !== 0 &&
                                <>
                                    <Text style={{ fontSize: 20, marginBottom: 20 }}><B>Completadas</B></Text>
                                    {completedAppointmentList?.items.map((app) => (
                                        <AppointmentCard
                                            key={app.id}
                                            appointment={app}
                                            onCancelPress={onappointmentCancel}
                                            onPress={appointmentClick} />
                                    ))}
                                    {completedAppointmentList?.hasNextPage === true && <Button
                                        style={{ marginBottom: 20 }}
                                        mode="text"
                                        onPress={() => fetchCompletedAppointments(completedAppointmentList.currentPage + 1)}>
                                        Ver más
                                    </Button>}
                                </>
                            }

                            {todayAppointmentList?.items?.length === 0 &&
                                pendingAppointmentList?.items?.length === 0 &&
                                confirmedAppointmentList?.items?.length === 0 &&
                                completedAppointmentList?.items?.length === 0 &&
                                <View
                                    style={{
                                        justifyContent: 'center',
                                        alignItems: 'center',
                                        marginTop: 20
                                    }}>
                                    <Image
                                        style={{
                                            height: 150,
                                            width: 150,
                                        }}
                                        source={Pet.calendar} />

                                    <Text style={{ fontSize: 20, textAlign: 'center' }}>
                                        Todavía no tienes citas programadas, ¡Empieza a buscar un psicólogo!
                                    </Text>
                                </View>
                            }
                        </>
                    }
                </View>
            </ScrollView>
        </SafeAreaView >
    );
}

export default Appointments;
